const ERROR_MESSAGES = {
	NO_NAV: 'Sticky Nav: Navigation element not found',
	INIT_FAILED: 'Sticky Nav: Initialization failed',
};

const CONFIG = {
	SELECTORS: {
		NAV: '[data-sticky-nav]',
		BUTTONS: '.sticky-nav__buttons',
		SCROLL_LEFT: '.scroll-left',
		SCROLL_RIGHT: '.scroll-right',
		NAV_BUTTON: '.sticky-nav__button',
		SCROLL_CONTAINER: '.sticky-nav__scroll-container',
	},
	CLASSES: {
		STICKY: 'is-sticky',
		ACTIVE: 'active',
	},
	SCROLL: {
		AMOUNT: 300,
		BREAKPOINT: 1280,
	},
};

/**
 * Handle sticky behavior of navigation
 * @param {HTMLElement} nav - The navigation element
 * @param {number} navTop - The original top position of the nav
 */
const handleSticky = (nav, navTop) => {
	if (window.pageYOffset > navTop) {
		nav.classList.add(CONFIG.CLASSES.STICKY);
	} else {
		nav.classList.remove(CONFIG.CLASSES.STICKY);
	}
};

/**
 * Scroll to the target section when a nav button is clicked
 * @param {HTMLElement} button - The clicked button
 * @param {HTMLElement} nav - The navigation element
 */
const scrollToSection = (button, nav) => {
	const targetId = button.getAttribute('data-scroll-to');
	if (!targetId) return;
	const targetSection = document.querySelector(`${targetId}`);
	if (!targetSection) return;

	const navHeight = nav.offsetHeight;
	const headerHeight = document.querySelector('header')?.offsetHeight || 0;
	const targetTop = targetSection.offsetTop - (navHeight + headerHeight + 50);

	window.scrollTo({
		top: targetTop,
		behavior: 'smooth',
	});
};

/**
 * Update active button based on scroll position
 * @param {NodeList} buttons - The navigation buttons
 * @param {HTMLElement} nav - The navigation element
 */
const updateActiveButton = (buttons, nav) => {
	const navHeight = nav.offsetHeight;
	const headerHeight = document.querySelector('header')?.offsetHeight || 0;
	const scrollPosition = window.pageYOffset + navHeight + headerHeight + 150;

	buttons.forEach((button) => {
		const targetId = button.getAttribute('data-scroll-to');
		if (!targetId) return;
		const targetSection = document.querySelector(`${targetId}`);
		if (!targetSection) return;

		const sectionTop = targetSection.offsetTop;
		const sectionBottom = sectionTop + targetSection.offsetHeight;

		if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
			buttons.forEach((btn) => btn.classList.remove(CONFIG.CLASSES.ACTIVE));
			button.classList.add(CONFIG.CLASSES.ACTIVE);

			const scrollContainer = nav.querySelector(CONFIG.SELECTORS.SCROLL_CONTAINER);
            if (scrollContainer) {
                button.scrollIntoView({
                    behavior: 'smooth',
                    inline: 'center',
                    block: 'nearest'
                });
            }
		}
	});
};

/**
 * Initialize the sticky navigation functionality
 * @returns {void}
 */
const initializeStickyNav = () => {
	const nav = document.querySelector(CONFIG.SELECTORS.NAV);
	if (!nav) {
		console.warn(ERROR_MESSAGES.NO_NAV);
		return;
	}

	try {
		const buttons = nav.querySelector(CONFIG.SELECTORS.BUTTONS);
		const scrollLeftBtn = nav.querySelector(CONFIG.SELECTORS.SCROLL_LEFT);
		const scrollRightBtn = nav.querySelector(CONFIG.SELECTORS.SCROLL_RIGHT);
		const navButtons = nav.querySelectorAll(CONFIG.SELECTORS.NAV_BUTTON);
		const scrollContainer = nav.querySelector(CONFIG.SELECTORS.SCROLL_CONTAINER);
		const navTop = nav.offsetTop;

		const updateScrollButtonsVisibility = () => {
			if (!buttons || !scrollLeftBtn || !scrollRightBtn || !scrollContainer) return;

			const needsScroll = buttons.scrollWidth > buttons.clientWidth;

			if (!needsScroll) {
				scrollLeftBtn.style.display = 'none';
				scrollRightBtn.style.display = 'none';
				scrollContainer.classList.add('p-0');
			} else {
				scrollLeftBtn.style.display = 'flex';
				scrollRightBtn.style.display = 'flex';
				scrollContainer.classList.remove('p-0');
			}
		};

		// Handle sticky behavior
		window.addEventListener('scroll', () => handleSticky(nav, navTop));

		// Handle scroll buttons
		scrollLeftBtn.addEventListener('click', () => {
			buttons.scrollBy({
				left: -CONFIG.SCROLL.AMOUNT,
				behavior: 'smooth',
			});
		});

		scrollRightBtn.addEventListener('click', () => {
			buttons.scrollBy({
				left: CONFIG.SCROLL.AMOUNT,
				behavior: 'smooth',
			});
		});

		updateScrollButtonsVisibility();
		window.addEventListener('resize', updateScrollButtonsVisibility);
		// Handle button clicks and scroll spy
		navButtons.forEach((button) => {
			button.addEventListener('click', (e) => {
				e.preventDefault();
				scrollToSection(button, nav);
			});
		});

		// Initialize scroll spy
		window.addEventListener('scroll', () => updateActiveButton(navButtons, nav));

		// Initial active state
		updateActiveButton(navButtons, nav);
	} catch (error) {
		console.error(ERROR_MESSAGES.INIT_FAILED, error);
	}
};

export { initializeStickyNav };
